/**
 * Browser-side data the Desktop keeps between visits (DCM-377).
 *
 * Screen stores cache their lists in IndexedDB (and day stamps in localStorage).
 * That storage belongs to the browser, not to the signed-in user, so it must be
 * wiped on sign-out and whenever a different account signs in on the same browser
 * — otherwise the next user's screens can hold the previous user's rows, including
 * centres outside their scope. MSAL keeps its tokens in sessionStorage
 * (app.module cacheLocation), so clearing localStorage does not sign anyone out.
 */

/** localStorage key holding the homeAccountId whose data the browser caches belong to. */
export const CACHE_OWNER_KEY = 'gtlcCacheOwner';

/** Deletes every IndexedDB database and clears localStorage. Best-effort: never throws. */
export async function wipeAppCaches(): Promise<void> {
  try {
    const dbs = await indexedDB.databases();
    await Promise.all(
      dbs
        .filter((db) => db.name)
        .map(
          (db) =>
            new Promise<void>((resolve) => {
              const req = indexedDB.deleteDatabase(db.name!);
              req.onsuccess = () => resolve();
              req.onerror = () => resolve();
              req.onblocked = () => resolve();
            }),
        ),
    );
  } catch {
    // indexedDB.databases() is unavailable in some browsers/private modes — best-effort.
  }
  try {
    localStorage.clear();
  } catch {
    // storage blocked — nothing cached there either.
  }
}

/** True when the browser caches already belong to this account. */
export function cacheOwnedBy(accountId: string): boolean {
  try {
    return localStorage.getItem(CACHE_OWNER_KEY) === accountId;
  } catch {
    return true; // no localStorage → no localStorage cache to leak; IndexedDB wipe would loop on reload
  }
}

/** Records the account the (now empty) caches belong to. */
export function setCacheOwner(accountId: string): void {
  try {
    localStorage.setItem(CACHE_OWNER_KEY, accountId);
  } catch {
    // best-effort
  }
}

/** Full reload so no store keeps an in-memory copy or an open handle on a deleted database. */
export function reloadPage(): void {
  window.location.reload();
}
